import { createContext, PropsWithChildren, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { TransitionGroup } from "react-transition-group";
// eslint-disable-next-line import/no-extraneous-dependencies
import { jwtDecode } from "jwt-decode";

import TransferDetails, {
  STEP_CONSENT,
  STEP_DESTINATION_ACCOUNT,
  STEP_INITIATE_TRANSFER,
  STEP_IRA_EXIT_SCREEN,
  STEP_MAILING_ADDRESS,
  STEP_MARTIAL_INFO,
  STEP_OLD_ACCOUNT_SPONSOR_NAME,
  STEP_OPEN_IRA,
  STEP_PERSONAL_INFO,
  STEP_POST_TRANSFER,
  STEP_SCHEDULE_PHONE_CALL,
  STEP_SCHEDULE_PHONE_CALL_CONFIRMATION,
  STEP_TRANSFER_INFO,
  STEP_OLD_ACCOUNT_CONFIRM,
  TransferResponse,
  TransferStatusTypes,
  TransferStep,
  STEP_SCHEDULE_PHONE_CALL_NO_LOADING,
  STEP_CONFIRM_TRANSFER_DETAILS,
  STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT,
  STEP_OLD_ACCOUNT_UPLOAD_STATEMENT,
  STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED,
  STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS,
  STEP_OLD_ACCOUNT_PROVIDER_SEARCH,
  STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN,
  STEP_DESTINATION_OPEN_AND_CONNECT_IRA,
  STEP_DESTINATION_HELP_RESULT,
  STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT,
  STEP_ANNUITY_CONTACT,
  STEP_OLD_ACCOUNT_COMPLETED_HOME,
  STEP_IDENTITY_VERIFICATION,
  STEP_IDENTITY_VERIFICATION_PERSONA,
  STEP_SPOUSE_NOTARY,
  STEP_TRANSFER_TASKS,
  NETWORK_ERROR_SCREEN,
} from "../class/transferDetails";
import UserDetails from "../class/userDetails";
import DestinationAccount from "../class/destinationAccount";
import MainAppContainer from "../layout/main-app-container";
import HeaderController from "../widgets/general/header-controller";
import ProgressBarController from "../widgets/general/progress-bar-controller";
import sendClose from "../utils/sendClose";
import { AppContext } from "./AppContext";
import { createDemoAPIInstance, createEmployerAPIInstance } from "../api";
import SourceAccount from "../class/sourceAccount";
import { StaticDataList, UserDetailsAPIResponse } from "../api/APIConfirmTransfer";
import getTransferProgress from "../utils/getTransferProgress";
import { DropdownOption } from "../components/dropdown/Dropdown";
import transformTransferStatus from "../utils/transformTransferStatus";

import { ConfirmTransferStep, steps } from "../widgets/confirm-transfer/useConfirmTransfer";
import Navigation from "../widgets/general/home-screen/Navigation";
import { TransferIntent } from "../class/transferIntent";
import { CalendlyExitButton, CalendlyWrapper, PhoneCallLoadingScreen } from "../widgets/post-transfer/styles";

import { NewTransferStatusType, TransferInstruction } from "../class/transferInstruction";
import {
  BRIDGE_DEMO,
  BRIDGE_DEVELOPMENT,
  TRANSFER_STATUS_COMPLETED,
  TransferData,
  UNEXPECTED_ERROR_MESSAGE,
  UNAUTHORIZED_ERROR_MESSAGE,
  BRIDGE_PRODUCTION,
  BridgeEnv,
} from "../typings";
import sendSuccess from "../utils/sendSuccess";
import {
  citizenshipResponse,
  generateSamplePartialTransfer,
  generateSampleTransferInIntentResponse,
  generateSampleTransferOutIntentResponse,
  stateResponse,
  suffixResponse,
} from "../api/mockAPIResponse";
import { sendSentryError } from "../lib/sentry";

import Analytics from "../utils/productAnalytics";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import { fallbackProviderList } from "./bridge-demo-data";
import parseUrl from "../utils/parseUrl";
import { COLOR_MANIFEST_TEAL } from "../styles/globalStylesVariables";
import Spinner from "../components/spinner";
import { ReactSelectOptions, ResolvedPromise } from "../components/types";
import getTransferOptionContent, {
  TransferOptionContent,
  TransferOptionsResponse,
  TransferOptionValues,
} from "../utils/getTransferOptionContent";
import { IRARecommendationContent, IRASurveyData } from "../class/iraQuestionnaire";
import { getProviderContent } from "../utils/getProviderContent";
import Modal from "../widgets/general/modal";
import { FileScanResponse, FileUploadStatus, SCAN_FAILED, UPLOAD_FAILED, UPLOAD_SUCCESS } from "../class/file";
import { type UploadedFile } from "@manifesthhqinc/ui-library";
import manualWait from "../utils/manualWait";
import axiosWithRetry from "../utils/axiosRetry";
import { create, type AxiosInstance } from "axios";
import mixpanel from "mixpanel-browser";
import { MockTask, TransferTask } from "../class/transferTask";

export type SourceOfDestination = "USER" | "CLIENT_FIX" | "CLIENT_OPTIONS";

const isConnectOldAccountSteps = (step: TransferStep): boolean => {
  return [
    STEP_OLD_ACCOUNT_SPONSOR_NAME,
    STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT,
    STEP_OLD_ACCOUNT_UPLOAD_STATEMENT,
    STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED,
    STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS,
    STEP_OLD_ACCOUNT_PROVIDER_SEARCH,
    STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN,
  ].includes(step);
};

export type TransferProviderValue = ReturnType<typeof CreateProviderValue>;

export type EmployerAPI = ReturnType<typeof createEmployerAPIInstance> | ReturnType<typeof createDemoAPIInstance>;

export const TransferInStep: TransferStep[] = [
  STEP_CONSENT,
  STEP_TRANSFER_INFO,
  STEP_OLD_ACCOUNT_SPONSOR_NAME,
  STEP_DESTINATION_ACCOUNT,
  STEP_PERSONAL_INFO,
  STEP_MAILING_ADDRESS,
  STEP_MARTIAL_INFO,
  STEP_INITIATE_TRANSFER,
  STEP_POST_TRANSFER,
];

export type ModalType = "Restart_Henry" | "Confirm_Address";

export const CreateProviderValue = () => {
  const {
    transferIntentId,
    appEnv,
    isAppLoading,
    isWebview,
    setPartnerConfig,
    setIsAppLoading,
    setVersion,
    version,
    setAppEnv,
    parentUrl,
    transferId,
    setTransferIntentId,
    setTransferId,
    setFlow,
    shouldAllowAddOldAccount,
    shouldFetchTransferTask,
  } = useContext(AppContext);

  const [userId, setUserId] = useState<string>("");
  const [transferDetails, setTransferDetails] = useState<TransferDetails>(new TransferDetails()); // Source of truth
  const [currentOldAccount, setCurrentOldAccount] = useState<SourceAccount>(new SourceAccount()); // Due to multiple source accounts, we need to control which source account user should be editing / adding
  const [userDetails, setUserDetails] = useState<UserDetails>(new UserDetails()); // user details

  const [suffixList, setSuffixList] = useState<DropdownOption[]>([]);
  const [citizenshipList, setCitizenshipList] = useState<DropdownOption[]>([]);
  const [statesList, setStatesList] = useState<DropdownOption[]>([]);

  const [shouldShowHomeScreen, setShouldShowHomeScreen] = useState<boolean>(false); // user flow
  const [screenHistory, setScreenHistory] = useState<TransferStep[]>([]);
  const [currentScreen, setCurrentScreen] = useState<any>(STEP_CONSENT); // user flow
  const [hasFailedTask, setHasFailedTask] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false); // UI
  const [progressBar, setProgressBar] = useState<{
    progress: number;
    shouldShowProgressBar: boolean;
  }>({
    progress: 0,
    shouldShowProgressBar: false,
  }); // UI
  const [showNavigation, setShowNavigation] = useState<boolean>(false);
  const [showRemovePopup, setShowRemovePopup] = useState<boolean>(false);
  const [employerAPI] = useState<EmployerAPI>(createEmployerAPIInstance());
  const [isStaticDataFetched, setIsStaticDataFetched] = useState<boolean>(false);
  const [screenRef, setScreenRef] = useState<any>();
  const [currentConfirmTransferStep, setCurrentConfirmTransferStep] = useState<ConfirmTransferStep>(steps[0]);
  const [showCalendly, setShowCalendly] = useState<boolean>(false);
  const [showCalendlyButton, setShowCalendlyButton] = useState<boolean>(false);
  const [transferInstructions, setTransferInstructions] = useState<TransferInstruction[]>([]);
  const [transferIntentDetails, setTransferIntentDetails] = useState<TransferIntent>();
  const [canStart, setCanStart] = useState<boolean>(false);
  const [showError, setShowError] = useState<string | undefined>(undefined);
  const [userPhoneNumber, setUserPhoneNumber] = useState<string>(transferIntentDetails?.userPhoneNumber || "");
  const [providerList, setProviderList] = useState<string[]>(fallbackProviderList);
  const [isPhoneCallNeeded, setIsPhoneCallNeeded] = useState<boolean>(true);
  const [validatedPhoneNumber, setValidatedPhoneNumber] = useState<{ message: string } | undefined>(undefined);
  const [phoneCallLoading, setPhoneCallLoading] = useState<boolean>(false);
  const [transferOptions, setTransferOptions] = useState<TransferOptionsResponse | null>(null);
  const [transferOptionContent, setTransferOptionContent] = useState<TransferOptionContent | null>(null);
  const [userSelectedTransferOption, setUserSelectedTransferOption] = useState<TransferOptionValues>(
    TransferOptionValues.ira,
  );
  const [iraRecommendationContent, setIraRecommendationContent] = useState<IRARecommendationContent | null>(null);

  const [cachedEmployers, setCachedEmployers] = useState<
    ResolvedPromise<typeof employerAPI.employer.fetchEmployersList>
  >([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ReactNode>(<></>);
  const [iraOpeningId, setIraOpeningId] = useState<string>("");
  const [hasMoreThanOneSourceAccount, setHasMoreThanOneSourceAccount] = useState<boolean>(false);

  // Loading Animation
  const [isDestinationAnimationLoaded, setIsDestinationAnimationLoaded] = useState<boolean>(false);
  const [isOldAccountAnimationLoaded, setIsOldAccountAnimationLoaded] = useState<boolean>(false);
  const [isIRAFoundAnimationLoaded, setIsIRAFoundAnimationLoaded] = useState<boolean>(false);

  const [apiInstance, setAPIInstance] = useState<AxiosInstance>(() => create({}));
  const [sourceOfDestination, setSourceOfDestination] = useState<SourceOfDestination | null>(null);

  const [isPendingIRA, setIsPendingIRA] = useState<boolean>(false);
  const [transferTasks, setTransferTasks] = useState<(TransferTask | MockTask)[]>([]);
  const [shouldSpouseInfoRequired, setShouldSpouseInfoRequired] = useState<boolean>(false);

  const getEmployerOptions = useCallback(
    async (userEmployerName: string): Promise<ReactSelectOptions> => {
      const employersResponse = await employerAPI.employer.fetchEmployersList(userEmployerName);

      const options = employersResponse.map((employer: any) => {
        let subtitle: string | undefined;
        if (employer.employerCity && employer.employerState) {
          subtitle = `${employer.employerCity}, ${employer.employerState}`;
        } else if (employer.employerState) {
          subtitle = employer.employerState;
        }
        return {
          label: employer.employerName,
          value: employer.employerId,
          employer,
          subtitle,
        };
      });

      // The autocomplete input caches the getEmployerOptions request, so we have to cache the
      // results manually and then figure out which employer was selected
      setCachedEmployers([...cachedEmployers, ...employersResponse]);
      return options;
    },
    [cachedEmployers],
  );

  useEffect(() => {
    if (transferIntentDetails?.userPhoneNumber) {
      setUserPhoneNumber(transferIntentDetails.userPhoneNumber);
    }
  }, [transferIntentDetails?.userPhoneNumber]);

  useEffect(() => {
    if (!currentScreen.includes("LOADING")) {
      const navigation = [...screenHistory, currentScreen];
      setScreenHistory(navigation);
    }
    if (isStaticDataFetched) return;

    const fetchStaticData = async () => {
      const [suffixRes, statesRes, citizenshipRes] = await Promise.all(
        [fetchSuffix, fetchStatesList, fetchCitizenshipList].map((e) => e()),
      );
      // TODO: create a transform function for static data list to dropdown option
      setSuffixList(
        suffixRes.map((e: string) => {
          return {
            label: e,
            value: e,
            id: e,
          };
        }),
      );
      setCitizenshipList(
        citizenshipRes.map((e: string) => {
          return {
            label: e,
            value: e,
            id: e,
          };
        }),
      );
      setStatesList(
        statesRes.map((e: string) => {
          return {
            label: e,
            value: e,
            id: e,
          };
        }),
      );

      setIsStaticDataFetched(true);
    };
    if (currentScreen === STEP_PERSONAL_INFO) {
      fetchStaticData();
    }
  }, [currentScreen]);

  const goToPreviousScreen = () => {
    const updatedScreenHistory = screenHistory;
    let previousScreen = updatedScreenHistory.pop();

    if (previousScreen === currentScreen) {
      previousScreen = updatedScreenHistory.pop();
    }
    setScreenHistory(updatedScreenHistory);
    if (previousScreen) {
      goToScreen(previousScreen);
    }
  };

  const updateUserDetails = (params: Partial<UserDetails>) => {
    setUserDetails(new UserDetails({ ...userDetails, ...params }));
  };

  const updateTransferDetails = (params: Partial<TransferDetails>) => {
    setTransferDetails(new TransferDetails({ ...transferDetails, ...params }));
  };

  const startTransferScreen = async () => {
    Analytics.track({ name: "Clicked Continue on Transfer Info Screen" });
    if (transferIntentId === "bridge-demo" || appEnv === BRIDGE_DEMO) {
      setProviderList(fallbackProviderList);
      setUserId("bridge-user-id");
      if (transferIntentId.includes("incomplete")) {
        setTransferDetails(
          new TransferDetails({
            transferStatus: TransferStatusTypes.inProgress,
            transferId: 9999,
            sourceAccounts: [
              new SourceAccount({
                sourceAccountId: 1000,
                planSponsor: "University of Chicago",
              }),
            ],
          }),
        );
        setUserDetails(new UserDetails({ firstName: "Katie" }));
        setShouldShowHomeScreen(true);
      } else if (transferIntentDetails?.sourceAccounts && transferIntentDetails?.sourceAccounts.length > 0) {
        // Show confirm source account screen
        goToScreen(STEP_OLD_ACCOUNT_CONFIRM);
      } else {
        goToScreen(STEP_OLD_ACCOUNT_SPONSOR_NAME);
      }

      return;
    }
    if (transferDetails.transferId !== -1 && transferDetails.sourceAccounts.length > 0) {
      getUserTransferOptions();
      setShouldShowHomeScreen(true);
    } else if (transferIntentDetails?.sourceAccounts && transferIntentDetails?.sourceAccounts.length > 0) {
      // Show confirm source account screen
      goToScreen(STEP_OLD_ACCOUNT_CONFIRM);
    } else {
      goToScreen(STEP_OLD_ACCOUNT_SPONSOR_NAME);
    }
  };

  const goToScreen = (step: TransferStep) => {
    setCurrentScreen(step);
    // set progress bar
    renderProgressBar(step);
  };

  const renderProgressBar = (step: TransferStep) => {
    if (
      step === STEP_CONSENT ||
      step === STEP_TRANSFER_INFO ||
      step === STEP_POST_TRANSFER ||
      step === STEP_SCHEDULE_PHONE_CALL ||
      step === STEP_SCHEDULE_PHONE_CALL_NO_LOADING ||
      step === STEP_SCHEDULE_PHONE_CALL_CONFIRMATION ||
      step === STEP_IRA_EXIT_SCREEN ||
      step === STEP_OPEN_IRA ||
      step === STEP_ANNUITY_CONTACT ||
      step === STEP_IDENTITY_VERIFICATION ||
      step === STEP_IDENTITY_VERIFICATION_PERSONA ||
      step === STEP_TRANSFER_TASKS ||
      step === STEP_SPOUSE_NOTARY
    ) {
      setProgressBar({
        shouldShowProgressBar: false,
        progress: 0,
      });
    } else {
      setProgressBar({
        shouldShowProgressBar: true,
        progress:
          (step === STEP_OLD_ACCOUNT_SPONSOR_NAME &&
            transferDetails.sourceAccounts.filter((e) => e.isCompleted).length >= 1) ||
          (hasMoreThanOneSourceAccount && isConnectOldAccountSteps(step))
            ? 0.4
            : getTransferProgress(step),
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      setProgressBar({
        shouldShowProgressBar: false,
        progress: 0,
      });
    } else {
      renderProgressBar(currentScreen);
    }
  }, [isLoading]);

  useEffect(() => {
    if (shouldShowHomeScreen || showError) {
      setProgressBar({ progress: 0, shouldShowProgressBar: false });
    }
  }, [shouldShowHomeScreen, showError]);

  const toggleHomeScreen = () => setShouldShowHomeScreen(!shouldShowHomeScreen);

  const clickClose = () => {
    const $transferId = transferId || transferDetails.transferId;
    const transferIntentStatus =
      transferInstructions.length > 0
        ? TRANSFER_STATUS_COMPLETED
        : transformTransferStatus(transferDetails.transferStatus);
    const transferData: TransferData = {
      transferIntentId,
      userId: transferIntentStatus === TRANSFER_STATUS_COMPLETED ? userId : null,
      transferId: transferInstructions
        .filter((t) => t.transferId === $transferId)
        .map((instruction: TransferInstruction) => instruction.transferInstructionId),
    };

    sendClose(
      {
        transferData,
        transferIntentStatus,
      },
      isWebview,
      parentUrl,
    );
  };

  const updateUserDetailsToAPI = async () => {
    if (appEnv !== BRIDGE_DEMO) {
      try {
        let res;
        if (version === "New") {
          res = await apiInstance({
            method: "POST",
            url: `/api/userIdentity/${userId}/userDetails`,
            data: {
              ...UserDetails.toAPI(userDetails),
            },
          });
        } else {
          res = await apiInstance({
            method: "POST",
            url: `/api/userIdentity/${userId}/userDetails`,
            data: {
              ...UserDetails.toAPI(userDetails),
            },
          });
        }

        if (transferIntentDetails && transferIntentDetails.user) {
          UserDetails.combineIntentAndAPI({
            apiResponse: res.data.data,
            intentResponse: transferIntentDetails.user,
          });
        }

        setUserDetails(UserDetails.fromAPI(res.data.data as UserDetailsAPIResponse));
      } catch (e: any) {
        if (e.response?.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to update user details:  ${e?.message || ""}`);
      }
    }
  };

  const uploadSignatureToAPI = async (signatureDataURL: string): Promise<boolean> => {
    if (appEnv !== BRIDGE_DEMO) {
      try {
        if (!userId) {
          return false;
        }
        if (version === "New") {
          await apiInstance({
            url: `/api/userIdentity/${userId}/userDetails/uploadSignature`,
            method: "POST",
            data: {
              imageValue: signatureDataURL,
            },
          });
        } else {
          await apiInstance({
            url: `/api/userIdentity/${userId}/userDetails/uploadSignature`,
            method: "POST",
            data: {
              imageValue: signatureDataURL,
            },
          });
        }
        return true;
      } catch (e: any) {
        if (e.response?.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to upload signature:  ${e?.message || ""}`);
        return false;
      }
    } else {
      return true;
    }
  };

  const getUserDetailsById = async (id: string) => {
    try {
      const res = await apiInstance({
        method: "GET",
        url: `/api/userIdentity/${id}/userDetails`,
      });

      if (transferIntentDetails && transferIntentDetails.user) {
        const combinedUserDetails = UserDetails.combineIntentAndAPI({
          apiResponse: res.data.data.userDetails,
          intentResponse: transferIntentDetails.user,
        });
        setUserDetails(combinedUserDetails);
        return combinedUserDetails;
      } else {
        setUserDetails(res.data.data.userDetails);
        return res.data.data.userDetails;
      }
    } catch (e: any) {
      if (e.response?.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
      }
      sendSentryError(`Unable to get user details`);
    }
  };

  const getUserIdentityObjectById = async (api: AxiosInstance, id: string) => {
    try {
      const res = await api({
        method: "GET",
        url: `/api/userIdentity/${id}`,
      });

      setUserPhoneNumber(res.data.data.phoneNumber);
    } catch (e: any) {
      sendSentryError(`Unable to get user identity object`);
    }
  };

  const initiateTransferToAPI = async (): Promise<string | undefined> => {
    if (appEnv === BRIDGE_DEMO) {
      setTransferDetails(
        new TransferDetails({
          ...transferDetails,
          transferStatus: TransferStatusTypes.initiated,
          transferId: 10000,
        }),
      );
      setTransferInstructions(
        transferDetails.sourceAccounts.map((s: SourceAccount, index: number) => {
          return {
            transferInstructionId: index,
            transferExternalStatusDescription: "Manifest is processing your transfer; no action is needed.",
            transferInstructionStatus: NewTransferStatusType.PROCESSING,
            transferExternalStatus: "Processing",
            scheduledPhoneCall: null,
            source: s,
            destinations: [transferDetails.destinationAccount as DestinationAccount],
            transferId: transferDetails.transferId,
          };
        }),
      );
      setIsPhoneCallNeeded(false);
      sendSuccess(
        {
          transferIntentId: transferIntentId,
          transferId: [10000],
          userId: "jamie",
        },
        parentUrl,
      );
      return "INPROGRESS";
    } else {
      let tasks: (TransferTask | MockTask)[] = [];
      if (!userId || transferDetails.transferId === -1) return;
      try {
        const res = await apiInstance({
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/initiateTransfer`,
          method: "POST",
        });
        setTransferDetails(TransferDetails.fromAPI(res.data.data as TransferResponse));
        if (appEnv === BRIDGE_PRODUCTION) {
          const taskRes = await getTransferTasks(transferDetails.transferId);

          tasks = taskRes;
        } else {
          tasks = await getTransferTasksFromTransferIntent(transferDetails.transferIntentReference);
        }
        setTransferTasks(tasks);
      } catch (e: any) {
        if (e.response?.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        } else {
          setShowError(UNEXPECTED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to initiate transfer - ${transferDetails.transferId}: ${e?.message || ""}`);
      }
      try {
        const transferInstructionsRes = await apiInstance({
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/transferInstruction`,
          method: "GET",
        });
        setTransferInstructions(transferInstructionsRes.data.data);
        await checkSchedulePhoneCall(
          transferInstructionsRes.data.data.map(
            (transferInstruction: TransferInstruction) => transferInstruction.transferInstructionId,
          ),
        );
        sendSuccess(
          {
            transferIntentId,
            userId,
            transferId:
              transferInstructionsRes.data.data.map(
                (transferInstruction: TransferInstruction) => transferInstruction.transferInstructionId,
              ) || [],
          },
          parentUrl,
        );
        let status = "INPROGRESS";
        transferInstructionsRes.data.data.forEach((t: TransferInstruction) => {
          if (t.transferExternalStatus.toLowerCase().includes("pending")) {
            status = "PENDING";
          }
        });

        const incompleteTasks = tasks.filter((e: TransferTask | MockTask) => {
          if ("isMock" in e) {
            return true;
          }
          return !e.completedOn;
        });
        if (incompleteTasks.length > 0) {
          status = "COMPLETETASK";
        }
        return status;
      } catch (e: any) {
        if (e.response?.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        } else {
          setShowError(UNEXPECTED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to fetch transfer instructions: ${e?.message || ""}`);
      }
    }
  };

  const onClickMenubutton = () => {
    setShowNavigation(true);
  };

  const navigateToHomeScreen = () => {
    setShouldShowHomeScreen(true);
    setShowNavigation(false);
  };

  const createDestainationInAPI = async (destinationAccountDetails?: DestinationAccount) => {
    // if (!transferIntentDetails?.transferAccount) return;
    if (!transferIntentDetails) return;
    let destinationAccount: DestinationAccount = new DestinationAccount();
    if (destinationAccountDetails) {
      destinationAccount = destinationAccountDetails;
    } else {
      // TODO: do something
    }

    if (appEnv === BRIDGE_DEMO) {
      // TODO: Check this
      updateTransferDetails(
        new TransferDetails({
          ...transferDetails,
          destinationAccount: destinationAccountDetails,
        }),
      );
    } else {
      // if (!userId || !transferIntentDetails?.transferAccount) return;
      try {
        if (destinationAccount.destinationAccountId !== -1) {
          await updateDestinationAccount(destinationAccount.destinationAccountId, destinationAccount);
        } else {
          await apiInstance({
            method: "POST",
            url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount`,
            data: {
              ...destinationAccount,
            },
          });
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        } else {
          setShowError(UNEXPECTED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to update destination account: ${transferDetails.transferId}. ${e?.message || ""}`);
      }

      await refreshTransferDetails();
    }
  };

  const refreshTransferDetails = async () => {
    if (!userId || transferDetails.transferId === -1) return;
    if (appEnv !== BRIDGE_DEMO) {
      try {
        const res = await apiInstance({
          method: "GET",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}`,
        });
        setTransferDetails(TransferDetails.fromAPI(res.data.data));
      } catch (e: any) {
        // do nothing
      }
    }
  };

  const fetchSuffix = async (): Promise<StaticDataList> => {
    if (!appEnv || appEnv === BRIDGE_DEMO) {
      return suffixResponse;
    } else {
      try {
        const res = await apiInstance({
          method: "GET",
          url: "/api/userDetails/suffix",
        });
        return res?.data?.data?.staticDataList || suffixResponse;
      } catch (e) {
        // do nothing
        return suffixResponse;
      }
    }
  };
  const fetchStatesList = async (): Promise<StaticDataList> => {
    if (!appEnv || appEnv === BRIDGE_DEMO) {
      return stateResponse;
    } else {
      try {
        const res = await apiInstance({
          method: "GET",
          url: "/api/userDetails/states",
        });
        return res?.data?.data?.staticDataList || stateResponse;
      } catch (e) {
        return stateResponse;
      }
    }
  };
  const fetchCitizenshipList = async (): Promise<StaticDataList> => {
    if (!appEnv || appEnv === BRIDGE_DEMO) {
      return citizenshipResponse;
    } else {
      try {
        const res = await apiInstance({
          method: "GET",
          url: "/api/userDetails/citizenship",
        });
        return res?.data?.data?.staticDataList || citizenshipResponse;
      } catch (e) {
        return citizenshipResponse;
      }
    }
  };
  const removeSourceAccount = async (sourceAccountId: SourceAccount["sourceAccountId"]) => {
    if (appEnv === BRIDGE_DEMO) {
      const updatedTransferDetails: TransferDetails = new TransferDetails({
        ...transferDetails,
        sourceAccounts: transferDetails.sourceAccounts.filter((e) => e.sourceAccountId !== sourceAccountId),
      });
      setTransferDetails(updatedTransferDetails);
    } else {
      try {
        await apiInstance({
          method: "DELETE",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/${sourceAccountId}`,
        });
      } catch (e: any) {
        sendSentryError(`Unable to delete source account - ${transferDetails.transferId}. ${e?.message || ""}`);
      }

      await refreshTransferDetails();
    }
  };

  const removeSourceAccountStatement = async (fileName: string) => {
    if (appEnv !== BRIDGE_DEMO) {
      try {
        await apiInstance({
          method: "DELETE",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/deleteStatement`,
          data: {
            statementFileName: fileName,
          },
        });
      } catch {
        // do nothing
      }
    }
  };

  const deleteDestinationAccountStatement = async (fileName: string) => {
    if (appEnv !== BRIDGE_DEMO) {
      try {
        await apiInstance({
          method: "DELETE",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount/deleteStatement`,
          data: {
            statementFileName: fileName,
          },
        });
      } catch {
        // do nothing
      }
    }
  };

  const exitCalendly = async () => {
    if (appEnv === BRIDGE_DEMO) {
      setCurrentScreen(STEP_SCHEDULE_PHONE_CALL_CONFIRMATION);
      setShowCalendly(false);
      document.getElementById("zohoBooking")!.innerHTML = "";
      const updatedTransferInstructions = transferInstructions.map((e) => {
        if (e.transferInstructionStatus === NewTransferStatusType.SCHEDULE_PHONE_CALL) {
          return {
            ...e,
            transferInstructionStatus: NewTransferStatusType.PROCESSING,
            transferExternalStatus: "Processing",
            transferExternalStatusDescription: "Manifest is processing your transfer; no action is needed.",
          };
        }
        return e;
      });
      setTransferInstructions(updatedTransferInstructions);
      return;
    }
    setPhoneCallLoading(true);
    // Call schedule phone call api
    let updatedPhoneCallNeeded = true;
    if (transferInstructions.length !== 0) {
      updatedPhoneCallNeeded = await checkSchedulePhoneCall(transferInstructions.map((t) => t.transferInstructionId));
    }
    if (transferInstructions.length > 0 && !updatedPhoneCallNeeded) {
      Analytics.track({
        name: "Finished Schedule phone call",
      });
      setCurrentScreen(STEP_SCHEDULE_PHONE_CALL_CONFIRMATION);
    }
    document.getElementById("zohoBooking")!.innerHTML = "";
    setShowCalendly(false);
    setPhoneCallLoading(false);
  };

  const scheduleZohoPhoneCallToAPI = async ({ transferInstructionId }: { transferInstructionId: string | number }) => {
    if (transferInstructions.length === 0 || !userId || appEnv === BRIDGE_DEMO) return;
    try {
      await apiInstance({
        method: "POST",
        url: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/schedulePhoneCall`,
      });
    } catch (e: any) {
      if (e.response?.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
      } else {
        setShowError(UNEXPECTED_ERROR_MESSAGE);
      }
      sendSentryError(
        `Unable to update schedule phone call - ${transferInstructions.map((t) => t.transferInstructionId).join(",")}`,
      );
    }
  };

  const fetchProviderList = async (api: AxiosInstance) => {
    if (providerList.length > 0) return;
    // TODO: put it in demo
    // if (appEnv === BRIDGE_DEMO) {
    //   setProviderList(fallbackProviderList);
    //   return;
    // }
    try {
      const res = await api({
        method: "GET",
        url: `/api/retirementSavingsData/providerName`,
      });
      setProviderList(res.data.data.staticDataList);
    } catch {
      sendSentryError("Unable to fetch list of provider. Proceed with fall back provider list");
      setProviderList(fallbackProviderList);
    }
  };

  const updateUserPhoneNumberToAPI = async (phoneNo: string) => {
    if (appEnv !== BRIDGE_DEMO && userId) {
      let email = transferIntentDetails?.user?.email;

      if (!email) {
        // get userIdentityObject
        const userIdentityObject = await apiInstance({
          method: "GET",
          url: `/api/userIdentity/${userId}`,
        });
        email = userIdentityObject.data.data.email;
      }
      try {
        await apiInstance({
          method: "POST",
          url: `/api/userIdentity/${userId}`,
          data: {
            phoneNumber: `+1${formatPhoneNumber(phoneNo)}`,
            userId,
            email,
          },
        });
      } catch (e) {
        // do nothing for now
      }
    }
  };

  const validatePhoneNumber = (): boolean => {
    const formatedPhoneNumber = formatPhoneNumber(userPhoneNumber);
    if (!userPhoneNumber || userPhoneNumber?.length === 0) {
      setValidatedPhoneNumber({
        message: "Please enter your phone number",
      });
      return false;
    } else if (formatedPhoneNumber.length < 10) {
      setValidatedPhoneNumber({
        message: "Please enter valid phone number",
      });
      return false;
    } else {
      setValidatedPhoneNumber(undefined);
      return true;
    }
    return true;
  };

  const checkSchedulePhoneCall = async (transferInstructionIds: string[] | number[]): Promise<boolean> => {
    await Promise.all(
      transferInstructionIds.map((transferInstructionId) => scheduleZohoPhoneCallToAPI({ transferInstructionId })),
    );
    // added get transfer instructions from API
    // get updated transfer instruction status
    let localUserId = userId;
    if (!localUserId) {
      const urlParams = parseUrl(window.location.href);
      localUserId = urlParams.userId;
    }
    try {
      const transferInstructionsRes = await apiInstance({
        url: `/api/userIdentity/${localUserId}/transfer/transferInstruction`,
        method: "GET",
      });
      setTransferInstructions(transferInstructionsRes.data.data);
      const isScheduleNeeded =
        transferInstructionsRes.data.data.filter(
          (transferInstruction: TransferInstruction) =>
            transferInstruction.transferInstructionStatus === NewTransferStatusType.SCHEDULE_PHONE_CALL,
        ).length > 0;
      setIsPhoneCallNeeded(isScheduleNeeded);
      return isScheduleNeeded;
    } catch (e: any) {
      if (e.response?.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
      } else {
        setShowError(UNEXPECTED_ERROR_MESSAGE);
      }
      return true;
    }
  };

  const sendHenryQuestionnaireData = async (data: any) => {
    try {
      if (appEnv === BRIDGE_DEMO) {
        setTransferOptions({
          FIRST: TransferOptionValues.ira,
          SECOND: TransferOptionValues.employerSponsored,
        });
        setTransferOptionContent({
          FIRST: getTransferOptionContent(TransferOptionValues.ira),
          SECOND: getTransferOptionContent(TransferOptionValues.employerSponsored),
        });
      } else {
        await apiInstance({
          method: "POST",
          url: `/api/userIdentity/${userId}/userPreference`,
          data,
        });
        const transferOptionsRes = await apiInstance({
          method: "GET",
          url: `/api/userIdentity/${userId}/userPreference/transferOptions`,
          data,
        });
        setTransferOptions({
          FIRST: transferOptionsRes.data.data.FIRST,
          SECOND: transferOptionsRes.data.data.SECOND,
        });
        setTransferOptionContent({
          FIRST: getTransferOptionContent(transferOptionsRes.data.data.FIRST),
          SECOND: getTransferOptionContent(transferOptionsRes.data.data.SECOND),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchIRARecommendation = async (
    data: IRASurveyData,
    iraType: TransferOptionValues.managedIRA | TransferOptionValues.ira,
  ) => {
    try {
      if (appEnv === BRIDGE_DEMO) {
        setIraRecommendationContent({
          FIRST: getProviderContent("FUTURE_MONEY"),
          SECOND: getProviderContent("BETTERMENT"),
          THIRD: getProviderContent("Fidelity"),
          FOURTH: getProviderContent("Charles Schwab"),
        });
      } else {
        const res = await apiInstance({
          method: "POST",
          url: `/api/userIdentity/${userId}/iraRecommendation`,
          data: {
            ...data,
            style: iraType === TransferOptionValues.managedIRA ? "HANDS_OFF" : "HANDS_ON",
          },
        });
        const { recommendation } = res.data.data;
        setIraRecommendationContent({
          FIRST: getProviderContent(recommendation.FIRST),
          SECOND: getProviderContent(recommendation.SECOND),
          THIRD: getProviderContent(iraType === TransferOptionValues.managedIRA ? "Fidelity" : "Vanguard"),
          FOURTH: getProviderContent(iraType === TransferOptionValues.managedIRA ? "Charles Schwab" : "Wealthfront"),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onClickContinueTransfer = () => {
    setShouldShowHomeScreen(false);

    if (transferDetails.sourceAccounts.length === 0) {
      goToScreen(STEP_OLD_ACCOUNT_CONFIRM);
    } else if (!transferDetails.destinationAccount) {
      if (shouldAllowAddOldAccount) {
        goToScreen(STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT);
      } else {
        goToScreen(STEP_OLD_ACCOUNT_COMPLETED_HOME);
      }
    } else if (
      transferDetails.destinationAccount &&
      (transferDetails.destinationAccount as DestinationAccount).yetToOpen &&
      (transferDetails.destinationAccount as DestinationAccount).providerName !== "Futuremoney" &&
      transferIntentDetails?.destinationAccounts?.filter(
        (d) => d.providerName === transferDetails.destinationAccount?.providerName,
      ).length === 0
    ) {
      goToScreen(STEP_DESTINATION_OPEN_AND_CONNECT_IRA);
    } else if (transferOptions && !transferDetails.destinationAccount) {
      goToScreen(STEP_DESTINATION_HELP_RESULT);
    } else {
      goToScreen(STEP_CONFIRM_TRANSFER_DETAILS);
    }
  };
  // do something

  const getTransferProgressText = (details: TransferDetails): string => {
    if (
      details.destinationAccount &&
      (transferDetails.destinationAccount as DestinationAccount).yetToOpen &&
      (transferDetails.destinationAccount as DestinationAccount).providerName !== "Futuremoney"
    ) {
      return "Finish destination details";
    } else {
      return "Confirm transfer details";
    }
  };

  const uploadSourceAccountFile = async (file: UploadedFile): Promise<FileUploadStatus> => {
    if (!userId || transferDetails.transferId === -1) return UPLOAD_FAILED;
    const formData = new FormData();
    formData.append("file", file.file);
    try {
      await apiInstance({
        method: "POST",
        url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/uploadStatementFile/${file.fileName}`,
        data: formData,
      });
    } catch (e: any) {
      if (e.response?.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
      }

      sendSentryError(`Unable to upload source account statement. ${e?.message || ""}`);
      return UPLOAD_FAILED;
    }
    try {
      if (appEnv === BRIDGE_DEVELOPMENT) {
        return UPLOAD_SUCCESS;
      } else {
        await manualWait(2000);
        const scanDoc = async () =>
          await apiInstance({
            method: "GET",
            url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/uploadStatementFile/${file.fileName}/scanStatus`,
          });
        const res: FileScanResponse = await axiosWithRetry(scanDoc);
        return res.fileSafe ? UPLOAD_SUCCESS : SCAN_FAILED;
      }
    } catch (e) {
      console.error(e);
      return SCAN_FAILED;
    }
  };

  const uploadDestiationFile = async (file: UploadedFile): Promise<FileUploadStatus> => {
    if (!userId || transferDetails.transferId === -1) return UPLOAD_FAILED;
    const formData = new FormData();
    formData.append("file", file.file);
    try {
      await apiInstance({
        method: "POST",
        url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount/uploadStatementFile/${file.fileName}`,
        data: formData,
      });
    } catch (e: any) {
      if (e.response?.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
      }

      sendSentryError(`Unable to upload source account statement. ${e?.message || ""}`);
      return UPLOAD_FAILED;
    }
    try {
      if (appEnv === BRIDGE_DEVELOPMENT) {
        return UPLOAD_SUCCESS;
      } else {
        await manualWait(2000);
        const scanDoc = async () =>
          await apiInstance({
            method: "GET",
            url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount/uploadStatementFile/${file.fileName}/scanStatus`,
          });
        const res: FileScanResponse = await axiosWithRetry(scanDoc);
        return res.fileSafe ? UPLOAD_SUCCESS : SCAN_FAILED;
      }
    } catch (e) {
      console.error(e);
      return SCAN_FAILED;
    }
  };

  const updateFuturemoneyConsent = async () => {
    try {
      if (appEnv !== BRIDGE_DEMO) {
        if (!iraOpeningId) return;
        await apiInstance({
          method: "POST",
          url: `/api/userIdentity/${userId}/transfer/destinationAccount/iraAccountOpeningData/${iraOpeningId}/updateConsent`,
        });
      }
    } catch {
      // Do nothing now
    }
  };

  const updateIRAOpenFormData = async (data: Record<string, string>) => {
    if (appEnv !== BRIDGE_DEMO) {
      if (!transferDetails.destinationAccount?.destinationAccountId) return;
      try {
        const res = await apiInstance({
          method: "POST",
          data,
          url: `/api/userIdentity/${userId}/transfer/destinationAccount/${transferDetails.destinationAccount?.destinationAccountId}/iraAccountOpeningData`,
        });
        setIraOpeningId(res.data.data.iraAccountOpeningDataId);
      } catch {
        // TODO: do nothing now
      }
    }
  };

  const getUserTransferOptions = async (): Promise<null | TransferOptionsResponse> => {
    if (!userId) return null;
    if (appEnv === BRIDGE_DEMO) return null;
    try {
      const transferOptionsRes = await apiInstance({
        method: "GET",
        url: `/api/userIdentity/${userId}/userPreference/transferOptions`,
      });
      const options = transferOptionsRes.data.data;
      if (options) {
        setTransferOptions(options);
        setTransferOptionContent({
          FIRST: getTransferOptionContent(options.FIRST),
          SECOND: getTransferOptionContent(options.SECOND),
        });
      }
      return options;
    } catch {
      return null;
    }
  };

  const updateDestinationAccount = async (
    destinationAccountId: DestinationAccount["destinationAccountId"],
    updatedDetails: DestinationAccount,
  ) => {
    if (appEnv === BRIDGE_DEMO) {
      updateTransferDetails(
        new TransferDetails({
          ...transferDetails,
          destinationAccount: updatedDetails,
        }),
      );
    } else {
      try {
        await apiInstance({
          method: "POST",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount/${destinationAccountId}/update`,
          data: { ...updatedDetails },
        });
        await refreshTransferDetails();
      } catch (e: any) {
        sendSentryError(`Unable to update destination account - ${transferDetails.transferId}. ${e?.message || ""}`);
      }
    }
  };

  const deleteCurrentDestination = async (destinationAccountId: DestinationAccount["destinationAccountId"]) => {
    if (appEnv === BRIDGE_DEMO) {
      updateTransferDetails(new TransferDetails({ ...transferDetails, destinationAccount: null }));
    } else {
      try {
        await apiInstance({
          method: "DELETE",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/destinationAccount/${destinationAccountId}`,
        });
        await refreshTransferDetails();
      } catch (e: any) {
        sendSentryError(`Unable to delete destination account - ${transferDetails.transferId}. ${e?.message || ""}`);
      }
    }
  };

  const initBridge = async ({
    $transferIntentId,
    $accessToken,
    $clientId,
    env,
  }: {
    $transferIntentId?: string;
    $accessToken?: string;
    $clientId?: string;
    env: string;
  }): Promise<boolean> => {
    // default mixpanelId for dev
    let mixpanelId = "bd5d25f14e000177792ba847934b1685";
    if (process.env.REACT_APP_ENV === "production") {
      // this is the production project id
      mixpanelId = "4923764bd4dfd40af65dc365fd82f44b";
    }
    mixpanel.init(mixpanelId, {
      debug: false,
      track_pageview: false,
      persistence: "localStorage",
      record_sessions_percent: 100,
      record_heatmap_data: true,

      autocapture: {
        click: true,
        capture_text_content: true,
        capture_extra_attrs: ["data-button-title", "data-name", "data-current-url", "name"],
      },
      record_mask_text_selector: "",
    });

    if (env === BRIDGE_DEVELOPMENT || env === BRIDGE_DEMO) {
      mixpanel.opt_out_tracking();
    }
    if (!$accessToken || !$transferIntentId || !$clientId) return false;
    if (env === BRIDGE_DEMO) {
      const transferIntentRes = $transferIntentId.includes("transfer-in")
        ? generateSampleTransferInIntentResponse()
        : $transferIntentId.includes("annuity")
          ? generateSamplePartialTransfer()
          : generateSampleTransferOutIntentResponse();

      const transferIntentData = new TransferIntent(transferIntentRes);
      setTransferIntentDetails(transferIntentData);
      setUserDetails(new UserDetails({ firstName: "Katie", lastName: "Clark" }));
      setUserId("bridge-user-id");
      setPartnerConfig(transferIntentData.isAnnuityTransfer ? transferIntentData.subClient.name : "InvestCo");
      setFlow(transferIntentData.isAnnuityTransfer ? "Annuity" : "Rollover");
      setCanStart(true);
      setIsAppLoading(false);
      setVersion("New");
      setAppEnv(BRIDGE_DEMO);
      return true;
    }

    const api = create({
      baseURL: env === BRIDGE_PRODUCTION ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${$accessToken}`,
        "X-Auth-Dispatch": "USER",
        ClientId: $clientId,
      },
    });

    api.interceptors.response.use(
      (response) => response,
      (error) => {
        const isNetworkError = !error?.response && error?.message === "Network Error";
        const isStatus450 = error?.response?.status === 450;

        if (isNetworkError || isStatus450) {
          // handle the special case here
          // e.g. trigger a re-auth flow, show a modal, log out, etc.
          handleSpecialErrorCase(error);
        }

        return Promise.reject(error);
      },
    );
    setAPIInstance(() => api);
    try {
      // GET Transfer Intent Details
      const transferIntentDetailsRes = await api({
        method: "GET",
        url: `/v2/transferIntent/${$transferIntentId}`,
      });
      const transferIntentData = new TransferIntent(transferIntentDetailsRes.data);
      Analytics.setUserProfiles({
        userName: `${transferIntentData.user?.firstName || ""} ${transferIntentData.user?.lastName || ""}`,
        userEmail: transferIntentData.userEmail,
        partnerId: $clientId,
      });
      setShouldSpouseInfoRequired(transferIntentData.processingInstructions?.spousalConsentRequired || false);
      setTransferIntentDetails(transferIntentData);
      setTransferIntentId($transferIntentId);
      fetchProviderList(api);

      // GET User Details
      const decoded = jwtDecode($accessToken);
      const $userId = decoded.sub;
      if ($userId) {
        // Get user Details & Client name
        const userDetailsRes = await api({
          method: "GET",
          url: `/api/userIdentity/${$userId}/userDetails`,
        });
        const userData: UserDetails = userDetailsRes.data.data.userDetails;
        setUserDetails(userData);
        setUserId($userId);
        setPartnerConfig(userData.client.name);
        getUserIdentityObjectById(api, $userId);
      }

      const { status, isAnnuityTransfer } = transferIntentData;
      setFlow(isAnnuityTransfer ? "Annuity" : "Rollover");
      if (isAnnuityTransfer && transferIntentData.subClient?.name) {
        setPartnerConfig(transferIntentData.subClient.name);
      }
      if (status === "COMPLETE" || status === "INPROGRESS") {
        const transferDetailsRes = await api({
          method: "GET",
          url: `/api/userIdentity/transferIntent/${$transferIntentId}/transfer`,
        });
        const { transferId: $transferId } = transferDetailsRes.data.data;
        const transferDetailsUpdated = TransferDetails.fromAPI(transferDetailsRes.data.data);

        updateTransferDetails(transferDetailsUpdated);
        setTransferId($transferId);

        if (status === "COMPLETE") {
          const transferInstructionRes = await api({
            method: "GET",
            url: `/api/userIdentity/${$userId}/transfer/${$transferId}/transferInstruction`,
          });
          setTransferInstructions(transferInstructionRes.data.data);
          if (env === BRIDGE_PRODUCTION) {
            if (shouldFetchTransferTask) {
              const transferTasksRes = await api({
                url: `/api/transfer/${$transferId}/transferTask`,
                method: "GET",
              });
              setTransferTasks(transferTasksRes.data.data);
            }
          } else {
            let mockTasks: MockTask[] = [];
            if (transferIntentData.processingInstructions?.identityVerificationRequired) {
              mockTasks = [...mockTasks, { isMock: true, type: "IDENTITY_VERIFICATION" }];
            }
            if (transferIntentData.processingInstructions?.spousalConsentRequired) {
              mockTasks = [...mockTasks, { isMock: true, type: "SPOUSAL_CONSENT" }];
            }

            setTransferTasks(mockTasks);
          }
        }
      }
      setCanStart(true);
      setIsAppLoading(false);
      setVersion("New");
      const environment: BridgeEnv =
        env && env.toLowerCase().includes("production") ? BRIDGE_PRODUCTION : BRIDGE_DEVELOPMENT;
      setAppEnv(environment);

      return true;
    } catch (e) {
      console.error({ e });
      return false;
    }
  };

  const handleSpecialErrorCase = (error: any) => {
    console.error(error);
    setProgressBar({
      progress: 0,
      shouldShowProgressBar: false,
    });
    setCurrentScreen(NETWORK_ERROR_SCREEN);
  };

  const updateAPIInstance = ({
    accessToken,
    clientId,
    env,
  }: {
    accessToken: string;
    clientId: string;
    env: string;
  }) => {
    const api = create({
      baseURL: env === BRIDGE_PRODUCTION ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_API_BASE_URL,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ClientId: clientId,
        "X-Auth-Dispatch": "EXTERNAL",
      },
    });

    api.interceptors.response.use(
      (response) => response,
      (error) => {
        const isNetworkError = !error?.response && error?.message === "Network Error";
        const isStatus450 = error?.response?.status === 450;

        if (isNetworkError || isStatus450) {
          // handle the special case here
          // e.g. trigger a re-auth flow, show a modal, log out, etc.
          handleSpecialErrorCase(error);
        }

        return Promise.reject(error);
      },
    );

    setAPIInstance(() => api);
  };

  const getTransferTasks = async ($transferId?: string | number): Promise<TransferTask[]> => {
    let lTransferId = $transferId;
    if (!lTransferId) {
      lTransferId = transferDetails.transferId;
    }
    // add feature flag to fetch /transfer task
    if (shouldFetchTransferTask) {
      const res = await apiInstance({
        url: `/api/transfer/${lTransferId}/transferTask`,
        method: "GET",
      });
      setTransferTasks(res.data.data);
      return res.data.data;
    }
    return [];
  };

  const getTransferTasksFromTransferIntent = async ($transferIntentId: string): Promise<MockTask[]> => {
    let $transferIntentDetails = transferIntentDetails;
    if (!$transferIntentDetails?.transferIntentId) {
      // get transfer intent details
      const transferIntentRes = await apiInstance({
        url: `/v2/transferIntent/${$transferIntentId}`,
        method: "GET",
      });
      $transferIntentDetails = transferIntentRes.data.data;
    }
    if (!$transferIntentDetails?.processingInstructions) return [];
    let tasks: MockTask[] = [];
    if ($transferIntentDetails.processingInstructions.identityVerificationRequired) {
      tasks = [...tasks, { isMock: true, type: "IDENTITY_VERIFICATION" }];
    }
    if ($transferIntentDetails.processingInstructions.spousalConsentRequired) {
      tasks = [...tasks, { isMock: true, type: "SPOUSAL_CONSENT" }];
    }
    return tasks;
  };

  useEffect(() => {
    if (!transferIntentDetails) return;
    if (transferIntentDetails.destinationAccounts && transferIntentDetails.destinationAccounts.length === 0) {
      setSourceOfDestination("USER");
    } else if (transferIntentDetails?.hasPrimaryDestination) {
      setSourceOfDestination("CLIENT_FIX");
    } else {
      setSourceOfDestination("CLIENT_OPTIONS");
    }
  }, [transferIntentDetails?.hasPrimaryDestination, transferIntentDetails?.destinationAccounts]);

  const refreshTransferInstructions = async () => {
    if (!transferDetails.transferId || !userId) return;
    const res = await apiInstance({
      url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/transferInstruction`,
      method: "GET",
    });

    setTransferInstructions(res.data.data);
  };

  return {
    userDetails,
    transferDetails,
    updateUserDetails,
    updateTransferDetails,
    goToScreen,
    toggleHomeScreen,
    shouldShowHomeScreen,
    clickClose,
    onClickMenubutton,
    currentScreen,
    setCurrentScreen,
    isLoading,
    setIsLoading,
    setShouldShowHomeScreen,
    updateUserDetailsToAPI,
    uploadSignatureToAPI,
    initiateTransferToAPI,
    createDestainationInAPI,
    currentOldAccount,
    setCurrentOldAccount,
    refreshTransferDetails,
    fetchSuffix,
    fetchStatesList,
    fetchCitizenshipList,
    progressBar,
    isStaticDataFetched,
    setIsStaticDataFetched,
    startTransferScreen,
    showNavigation,
    setShowNavigation,
    screenRef,
    setScreenRef,
    suffixList,
    citizenshipList,
    statesList,
    employerAPI,
    showRemovePopup,
    setShowRemovePopup,
    currentConfirmTransferStep,
    setCurrentConfirmTransferStep,
    navigateToHomeScreen,
    userId,
    removeSourceAccount,
    setUserId,
    showCalendly,
    setShowCalendly,
    exitCalendly,
    showCalendlyButton,
    setShowCalendlyButton,
    transferInstructions,
    setTransferInstructions,
    transferIntentDetails,
    setTransferIntentDetails,
    getUserDetailsById,
    isAppLoading,
    setCanStart,
    canStart,
    showError,
    setShowError,
    updateUserPhoneNumberToAPI,
    getUserIdentityObjectById,
    validatePhoneNumber,
    userPhoneNumber,
    setUserPhoneNumber,
    validatedPhoneNumber,
    providerList,
    setShowBooking: setShowCalendly,
    setShowBookingButton: setShowCalendlyButton,
    exitBooking: exitCalendly,
    checkSchedulePhoneCall,
    isPhoneCallNeeded,
    phoneCallLoading,
    getEmployerOptions,
    sendHenryQuestionnaireData,
    transferOptions,
    transferOptionContent,
    userSelectedTransferOption,
    setUserSelectedTransferOption,
    fetchIRARecommendation,
    iraRecommendationContent,
    goToPreviousScreen,
    showModal,
    setShowModal,
    modalContent,
    setModalContent,
    onClickContinueTransfer,
    getTransferProgressText,
    uploadDestiationFile,
    updateFuturemoneyConsent,
    updateIRAOpenFormData,
    setHasMoreThanOneSourceAccount,
    getUserTransferOptions,
    deleteCurrentDestination,
    updateDestinationAccount,
    uploadSourceAccountFile,
    removeSourceAccountStatement,
    deleteDestinationAccountStatement,
    isDestinationAnimationLoaded,
    setIsDestinationAnimationLoaded,
    isOldAccountAnimationLoaded,
    setIsOldAccountAnimationLoaded,
    isIRAFoundAnimationLoaded,
    setIsIRAFoundAnimationLoaded,
    initBridge,
    apiInstance,
    updateAPIInstance,
    fetchProviderList,
    sourceOfDestination,
    setSourceOfDestination,
    isPendingIRA,
    setIsPendingIRA,
    getTransferTasks,
    transferTasks,
    setTransferTasks,
    shouldSpouseInfoRequired,
    handleSpecialErrorCase,
    refreshTransferInstructions,
    hasFailedTask,
    setHasFailedTask,
  };
};

export const TransferContext = createContext({} as TransferProviderValue);

const TransferProvider = (props: PropsWithChildren<any>) => {
  const providerValue = CreateProviderValue();
  const {
    setShowNavigation,
    showNavigation,
    clickClose,
    navigateToHomeScreen,
    showCalendly,
    isAppLoading,
    showModal,
    setShowModal,
    modalContent,
  } = providerValue;

  return (
    <TransferContext.Provider value={providerValue}>
      <MainAppContainer
        name="main-app-container"
        isLoading={isAppLoading}
        showModal={showModal}
        closeModal={() => setShowModal(false)}
      >
        {!isAppLoading && (
          <>
            <HeaderController />
            <Navigation
              closeNavigation={() => setShowNavigation(false)}
              sendClose={clickClose}
              show={showNavigation}
              goToHomeScreen={navigateToHomeScreen}
            />
            <ProgressBarController />
          </>
        )}
        {showCalendly && !providerValue.phoneCallLoading && (
          <CalendlyExitButton>
            <button name="exit-booking" onClick={providerValue.exitCalendly}>
              Exit Booking
            </button>
          </CalendlyExitButton>
        )}
        <CalendlyWrapper id="zohoBooking" $show={showCalendly} />
        {showCalendly && providerValue.phoneCallLoading && (
          <PhoneCallLoadingScreen>
            <Spinner size={35} color={COLOR_MANIFEST_TEAL} />
          </PhoneCallLoadingScreen>
        )}

        <TransitionGroup>
          {props.children}
          <Modal showModal={showModal} closeModal={() => setShowModal(false)}>
            {modalContent}
          </Modal>
        </TransitionGroup>
      </MainAppContainer>
    </TransferContext.Provider>
  );
};

export default TransferProvider;
